/**
 * InvoiceFlow — orchestrates the photo-invoice capture → extract → review flow.
 *
 * States (driven by useDocumentExtract + local UI state):
 *   idle        → show InvoiceCapture zone
 *   uploading   → spinner ("Uploading document…")
 *   redacting   → spinner ("Securing your document…") — DLP redaction in progress
 *   extracting  → spinner ("Reading your document…")
 *   done        → show InvoiceReview
 *   failed      → error + retry option
 *
 * Called from ServiceLog with vehicleId and currentUser.uid.
 */

import { F } from '@/design/fonts';
import { RADIUS, SPACE, T } from '@/design/tokens';
import { useDocumentExtract } from '@/hooks/useDocumentExtract';
import type { CSSProperties } from 'react';
import { InvoiceCapture } from './InvoiceCapture';
import { InvoiceReview } from './InvoiceReview';

interface InvoiceFlowProps {
  vehicleId: string;
  authorUid: string;
  onDone: () => void;
  onCancel: () => void;
}

function Spinner() {
  return (
    <div
      role="status"
      aria-label="Loading"
      style={{
        width: 32,
        height: 32,
        border: `3px solid color-mix(in srgb, ${T.accent} 20%, transparent)`,
        borderTopColor: T.accent,
        borderRadius: '50%',
        animation: 'gg-spin 0.8s linear infinite',
        margin: '0 auto',
      }}
    >
      <style>{`@keyframes gg-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export function InvoiceFlow({ vehicleId, authorUid, onDone, onCancel }: InvoiceFlowProps) {
  const { state, extract, reset } = useDocumentExtract(vehicleId);

  const wrapStyle: CSSProperties = {
    display: 'grid',
    gap: SPACE.md,
    fontFamily: F.body,
  };

  const secondaryBtnStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    color: T.textMuted,
    border: `1px solid ${T.border}`,
    borderRadius: RADIUS.md,
    padding: `${SPACE.sm}px ${SPACE.md}px`,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    minHeight: 44,
    width: '100%',
  };

  const primaryBtnStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: T.accent,
    color: T.onAccent,
    border: 'none',
    borderRadius: RADIUS.md,
    padding: `${SPACE.sm}px ${SPACE.md}px`,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    minHeight: 44,
    width: '100%',
  };

  if (state.status === 'idle') {
    return (
      <div style={wrapStyle}>
        <InvoiceCapture
          onFile={(file) => {
            void extract(file);
          }}
        />
        <button type="button" style={secondaryBtnStyle} onClick={onCancel}>
          Cancel
        </button>
      </div>
    );
  }

  if (state.status === 'uploading') {
    return (
      <div style={{ ...wrapStyle, textAlign: 'center' }}>
        <Spinner />
        <p style={{ margin: 0, color: T.textMuted, fontSize: 13 }}>Uploading document&hellip;</p>
      </div>
    );
  }

  if (state.status === 'redacting') {
    return (
      <div style={{ ...wrapStyle, textAlign: 'center' }}>
        <Spinner />
        <p style={{ margin: 0, color: T.textMuted, fontSize: 13 }}>
          Securing your document&hellip;
        </p>
      </div>
    );
  }

  if (state.status === 'extracting') {
    return (
      <div style={{ ...wrapStyle, textAlign: 'center' }}>
        <Spinner />
        <p style={{ margin: 0, color: T.text, fontSize: 14, fontWeight: 600 }}>
          Reading your document&hellip;
        </p>
        <p style={{ margin: 0, color: T.textMuted, fontSize: 12, lineHeight: 1.5 }}>
          This usually takes a few seconds. Hang tight.
        </p>
      </div>
    );
  }

  if (state.status === 'failed') {
    return (
      <div style={wrapStyle}>
        <div
          style={{
            background: T.card,
            border: `1px solid ${T.border}`,
            borderLeft: `3px solid ${T.orange}`,
            borderRadius: RADIUS.md,
            padding: SPACE.md,
          }}
        >
          <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: T.text }}>
            Couldn't read the document
          </p>
          <p
            style={{
              margin: `${SPACE.xs}px 0 0`,
              fontSize: 13,
              color: T.textMuted,
              lineHeight: 1.5,
            }}
          >
            {state.error}
          </p>
          <p
            style={{
              margin: `${SPACE.xs}px 0 0`,
              fontSize: 13,
              color: T.textMuted,
              lineHeight: 1.5,
            }}
          >
            Try a clearer photo or a different file, or add the entry manually.
          </p>
        </div>
        <button type="button" style={primaryBtnStyle} onClick={reset}>
          Try again
        </button>
        <button type="button" style={secondaryBtnStyle} onClick={onCancel}>
          Cancel
        </button>
      </div>
    );
  }

  // status === 'done'
  if (state.status === 'done') {
    return (
      <InvoiceReview
        vehicleId={vehicleId}
        authorUid={authorUid}
        imageRef={state.doc.imageRef}
        result={state.result}
        onDone={onDone}
        onRetry={reset}
      />
    );
  }

  return null;
}
